import React, { useState, useCallback, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaCarouselType } from 'embla-carousel';

import { Props as AccreditationCardProps } from './subcomponents/AccreditationCard/AccreditationCard';
import { Props as StatCardProps } from './subcomponents/StatCard/StatCard';

import VisuallyHidden from 'Elements/VisuallyHidden';

import AccreditationCard from './subcomponents/AccreditationCard';
import StatCard from './subcomponents/StatCard';
import CarouselIndicator from './subcomponents/CarouselIndicator/CarouselIndicator';

import {
    Container,
    EmblaCarousel,
    EmblaContainer,
    Slide,
    SliderButton,
    Control,
    ControlContainer,
    NavItem,
    NavList,
    Nav
} from './Carousel.styled';

export interface Item {
    id: string;
    type: 'accreditation' | 'stat';
    accreditation?: AccreditationCardProps;
    stat?: StatCardProps;
}

export interface Props {
    className?: string;
    id: string;
    label: string;
    items: Item[];
    showBorder?: boolean;
}

const Carousel: React.FC<Props> = ({ id, className, label, items, showBorder = true }) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: true,
        align: 'start',
        inViewThreshold: 0.8
    });
    const [slidesInView, setSlidesInView] = useState<number[]>([]);

    const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
    const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
    const scrollTo = useCallback(
        (index: number) => emblaApi && emblaApi.scrollTo(index),
        [emblaApi]
    );

    const onSlidesInView = useCallback((emblaApi: EmblaCarouselType) => {
        setSlidesInView(emblaApi.slidesInView());
    }, []);

    useEffect(() => {
        if (!emblaApi) return;

        onSlidesInView(emblaApi);
        emblaApi.on('slidesInView', onSlidesInView);
    }, [emblaApi, onSlidesInView]);

    return (
        <Container className={className} $showBorder={showBorder}>
            <VisuallyHidden>
                <a href={`#carousel_skip_${id}`}>Skip {label} carousel</a>
            </VisuallyHidden>
            <EmblaCarousel ref={emblaRef}>
                <EmblaContainer>
                    {items.map(({ id, ...item }, index) => (
                        <Slide key={`${id}-${index}`} tabIndex={0}>
                            {item.type === 'accreditation' && item.accreditation && (
                                <AccreditationCard {...item.accreditation} />
                            )}
                            {item.type === 'stat' && item.stat && <StatCard {...item.stat} />}
                        </Slide>
                    ))}
                </EmblaContainer>
            </EmblaCarousel>
            {items.length > 1 && (
                <>
                    <ControlContainer>
                        <Control aria-hidden>
                            <SliderButton
                                id={`carousel_${id}_next`}
                                next
                                handleOnClick={scrollNext}
                                tabIndex={-1}
                            />
                        </Control>
                        <Control aria-hidden>
                            <SliderButton
                                id={`carousel_${id}_previous`}
                                previous
                                handleOnClick={scrollPrev}
                                tabIndex={-1}
                            />
                        </Control>
                    </ControlContainer>
                    <Nav>
                        <NavList>
                            {items.map((item, index) => (
                                <NavItem key={`navitem_${item.id}`}>
                                    <CarouselIndicator
                                        aria-label={`Go to view ${index}`}
                                        aria-current={slidesInView.includes(index)}
                                        onClick={() => scrollTo(index)}
                                        isActive={slidesInView.includes(index)}
                                        tabIndex={-1}
                                    />
                                </NavItem>
                            ))}
                        </NavList>
                    </Nav>
                </>
            )}
            <span id={`carousel_skip_${id}`} />
        </Container>
    );
};

export default Carousel;
